/**
 * Events tracked by AnalyticsTracker
 *
 * Each of these events are tracked by AnalyticsTracker (./index.js)
 * name (function) and event (string) are required
 *
 * Please add a spec if you create a new event (analytics_tracker.spec.js)
 *
 * {
 *   name: (state, value, opts = {}) => String
 *      Function that returns the name of the emitted event
 *        state is model being listened to
 *        value is the value passed from the event (typically the new state property value)
 *        opts is an optional hash passed from the event
 *
 *   event: String
 *      The event being listened to
 *
 *   appState: String (optional)
 *      Identifier of the appState to listen to (defaults to the root app if blank)
 *
 *   filter: (state, value, opts = {}) => Boolean (optional)
 *      Do not fire this event unless filter return value is truthy
 *        state is model being listened to
 *        value is the value passed from the event (typically the new state property value)
 *        opts is an optional hash passed from the event
 *
 *   properties: (state, value, opts = {}) => Object (optional)
 *      Returns a hash of arbitrary properties to be passed to the event listener
 *        state is model being listened to
 *        value is the value passed from the event (typically the new state property value)
 *        opts is an optional hash passed from the event
 * }
 */

const events = [
  {
    name: () => 'feedback:submit',
    event: 'feedback:submit',
    properties: ({ rating, response }) => ({ rating, response })
  },
  {
    name: (_state, _activity, { action }) => action,
    appState: 'activities',
    event: 'change:activity',
    filter: (_state, _activity, opts = {}) => opts.action
  },
  {
    name: () => 'create_group',
    appState: 'activities',
    event: 'change:group',
    filter: (_, group) => group.isNew()
  },
  {
    name: () => 'activity:start_presentation',
    appState: 'activities',
    event: 'change:page',
    filter: (_, page) => page === 'presenter'
  },
  {
    name: () => 'activity:stop_presentation',
    appState: 'activities',
    event: 'change:page',
    filter: (state) => state.previous('page') === 'presenter'
  },
  {
    name: (_, action) => action,
    appState: 'activities',
    event: 'change:pageAction',
    filter: (_, action) => action
  },
  {
    name: () => 'bulk_select:open',
    appState: 'activities',
    event: 'change:selecting',
    filter: (_, selecting) => selecting
  },
  {
    name: () => 'bulk_select:close',
    appState: 'activities',
    event: 'change:selecting',
    filter: (_, selecting) => !selecting
  },
  {
    name: (_, action) => `bulk_select:${action}`,
    appState: 'activities',
    event: 'change:bulkAction',
    filter: (_, action) => action
  },
  {
    name: (_, insertionType) => `slideware:insertionType:${insertionType}`,
    appState: 'slideware',
    event: 'change:insertionType'
  },
  {
    name: (state) => `insert:${state.get('insertionType')}`,
    appState: 'slideware',
    event: 'change:page',
    filter: (_, page) => page === 'done'
  },
  {
    name: () => 'insert:webpage',
    appState: 'slideware',
    event: 'change:page',
    filter: (_, page) => page === 'insertWebPageDone'
  },
  {
    name: () => 'insert:video',
    appState: 'slideware',
    event: 'change:page',
    filter: (_, page) => page === 'insertVideoDone'
  },
  {
    name: () => 'slideware:clear_responses',
    appState: 'slideware',
    event: 'change:page',
    filter: (_, page) => page === 'clearResponses',
    properties: (state) => ({
      option: Array.isArray(state.get('activity')) ? 'all' : 'selected'
    })
  }
]

export default events
